/**
 * สูตรกลางเครดิต COD — mirror ของ bangbow-api-v2/src/routers/factory/credit-formulas.ts
 * ใช้เป็น fallback เมื่อ API ไม่ส่ง snapshot (เช่น cod-info ยังโหลดไม่เสร็จ)
 */

export type CodQuotaSnapshot = {
  available_credit: number;
  pending_cod_credit: number;
  multiplier: number;
  usable_credit: number;
  remaining_quota: number;
};

export function normalizeMultiplier(multiplier = 2): number {
  return Number(multiplier || 2) || 2;
}

export const toDisplayCredit = (raw: number | null | undefined, multiplier = 2): number =>
  Number(raw ?? 0) * normalizeMultiplier(multiplier);

export const toAmountDisplay = toDisplayCredit;

/** @deprecated ใช้ toUnitDeductReserve/toUnitDeductFail แทน — ดู credit-formulas.ts (backend) */
export function toUnitDeduct(cost: number | null | undefined, multiplier = 2): number {
  return Number(cost ?? 0) / normalizeMultiplier(multiplier);
}

export function normalizeReservePercent(percent: number | null | undefined): number {
  const p = Number(percent);
  if (!Number.isFinite(p) || p < 0 || p > 1) return 0.5;
  return p;
}

export function toUnitDeductReserve(cost: number | null | undefined, reservePercent: number | null | undefined): number {
  return Number(cost ?? 0) * normalizeReservePercent(reservePercent);
}

export function toUnitDeductFail(cost: number | null | undefined, reservePercentUsed: number | null | undefined): number {
  return Number(cost ?? 0) * (1 - normalizeReservePercent(reservePercentUsed));
}

export function getCreditDebtAmountFromAvailable(available: number | null | undefined): number {
  return Math.max(0, -Number(available ?? 0));
}

export function computeAccruedDebtRaw(availBefore: number, availAfter: number): number {
  return Math.max(0, -availAfter) - Math.max(0, -availBefore);
}

export function getQuotaSnapshot(
  available: number | null | undefined,
  pending: number | null | undefined,
  multiplier = 2,
): Pick<CodQuotaSnapshot, "usable_credit" | "remaining_quota"> {
  const mult = normalizeMultiplier(multiplier);
  const usable_credit = Math.max(0, toDisplayCredit(available, mult));
  const remaining_quota = usable_credit;
  return { usable_credit, remaining_quota };
}

/** ยอดเครดitใน lot หมดอายุเร็วสุดที่ใช้ได้จริง = min(lot×mult, remaining_quota); ถ้า remaining=0 → 0 */
export function getExpiringQuota(
  lotRemaining: number | null | undefined,
  remainingQuota: number | null | undefined,
  multiplier = 2,
): number {
  const usable = Math.max(0, Number(remainingQuota ?? 0));
  if (usable <= 0) return 0;
  const lotDisplay = Math.max(0, toDisplayCredit(lotRemaining, multiplier));
  return Math.min(lotDisplay, usable);
}

export function getSelectionQuota(
  remainingQuota: number,
  reservedCost: number,
  insufficientCost: number,
): { quota_before_selection: number; quota_after_selection: number } {
  const remaining = Number(remainingQuota ?? 0);
  return {
    quota_before_selection: remaining + Number(reservedCost ?? 0),
    quota_after_selection: remaining - Number(insufficientCost ?? 0),
  };
}
